import { BaseClickable, Stack, Text, Icons, Box } from '@solvhealth/jigsaw-v1';
import React, { useMemo, useRef } from 'react';
import { ALMA_CATEGORY } from '~/components/AccountGetsMarried/constants/alma';
import { ConsumerServiceCategory } from '~/components/AccountGetsMarried/constants/ConsumerServices/consumerServiceCategories';
import { analyticsTrackEvent } from '~/core/analytics';
import {
  ALMA_CARD_SEEN,
  ALMA_ENTRYPOINT_CLICKED,
  CONSUMER_SERVICES_ENTRYPOINT_ACCOUNT_HOME,
  CONSUMER_SERVICES_ENTRYPOINT_CATEGORY_LIST_PAGE,
  CONSUMER_SERVICE_CATEGORY_CARD_TOUCHED,
} from '~/core/analytics/events';
import { useLDFlags } from '~/core/launch-darkly/hooks/useLDFlags';
import { RemixRoutes } from '~/core/remix';
import { useObserveVisibility } from '~/hooks/useObserveVisibility';

type Props = {
  category: ConsumerServiceCategory;
  entrypoint:
    | typeof CONSUMER_SERVICES_ENTRYPOINT_ACCOUNT_HOME
    | typeof CONSUMER_SERVICES_ENTRYPOINT_CATEGORY_LIST_PAGE;
  /** Whether to use a pinkish background to make the cards stand out */
  tinted?: boolean;
};

type ServiceIconProps = {
  icon: keyof typeof Icons;
};
export const ServiceCategoryIcon = ({ icon }: ServiceIconProps) => {
  const Icon = Icons[icon];
  return <Icon color="grape-medium" justifyContent="flex-start" />;
};

function ServiceCategoryCard({ category, entrypoint, tinted }: Props) {
  const ref = useRef(null);
  const flags = useLDFlags();

  const href = useMemo(() => {
    if (category.slug === 'labs') {
      return RemixRoutes.labTests;
    }

    if (category.slug === ALMA_CATEGORY && flags.merry871MentalHealth) {
      return RemixRoutes.mentalHealth;
    }

    return `/services/${category.slug}`;
  }, [category.slug]);

  useObserveVisibility(ref, () => {
    if (category.slug === ALMA_CATEGORY) {
      analyticsTrackEvent(ALMA_CARD_SEEN, {
        entrypoint,
      });
    }
  });

  if (category.hidden) {
    return null;
  }

  return (
    <BaseClickable
      alignItems="start"
      bg={tinted ? 'grape-xlight' : 'white'}
      border={tinted ? 'solid 1px' : 'none'}
      borderColor="grape"
      borderRadius="base"
      boxShadow={tinted ? 'none' : 'sm'}
      event={CONSUMER_SERVICE_CATEGORY_CARD_TOUCHED}
      external={
        category.slug === 'labs' || (category.slug === ALMA_CATEGORY && flags.merry871MentalHealth)
      }
      height="100%"
      href={href}
      key={category.slug}
      meta={{
        category: category.slug,
        position: category.sortOrder,
        entrypoint,
        variant: 'card',
      }}
      onClick={() => {
        if (category.slug === ALMA_CATEGORY) {
          analyticsTrackEvent(ALMA_ENTRYPOINT_CLICKED, {
            entrypoint,
          });
        }
      }}
      p={2}
      ref={ref}
      style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
      width="100%"
    >
      <Stack flex={1} justifyContent="start" space={0.5}>
        <Stack alignItems="center" row space={1.5}>
          <ServiceCategoryIcon icon={category.icon} />
          {category.new && (
            <Text
              sx={{
                bg: tinted ? 'grape-medium' : 'crunchberry-xlight',
                textColor: tinted ? 'white' : 'crunchberry-dark',
                textTransform: 'uppercase',
                fontWeight: 'bold',
                px: 0.5,
                borderRadius: 'sm',
              }}
              textColor="crunchberry-dark"
              variant="caption"
            >
              New
            </Text>
          )}
        </Stack>
        <Text textColor={tinted ? 'grape-dark' : 'gray-600'} variant="h4">
          {category.name}
        </Text>
        <Text muted textColor={tinted ? 'grape-dark' : 'gray-600'} variant="body2">
          {category.examples}
        </Text>
      </Stack>
    </BaseClickable>
  );
}

export default ServiceCategoryCard;
